import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import cn from 'classnames';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { ButtonGroup } from 'reactstrap';
import { get } from 'lodash';

import { numberMask, cardNumberMask, numberUnmask } from 'utils/masks';
import { states } from 'utils/states';
import Select from 'components/Form/Select';
import Input from 'components/Form/Input';
import Validator from 'components/Validator/Validator';
import { Button } from 'components/Button';
import { addPaymentAccountAction, validateBankRoutingNumber } from 'actions/quickpay';
import masterCardIcon from 'assets/icons/mastercard.svg';
import visaCardIcon from 'assets/icons/visa.svg';
import amxIcon from 'assets/icons/amex.svg';
import discoverIcon from 'assets/icons/discover.svg';
import VerifiedIcon from 'assets/images/correct.png';
import WarningIcon from 'assets/images/warning.png';

import addressSchema from './schemas/addressSchema';
import addDebitCardSchema from './schemas/addDebitCardSchema';
import addPaymentMethodSchema from './schemas/addPaymentMethodSchema';
import styles from './styles.module.scss';

const INITIAL_VALIDATION_STATE = {
  loading: false,
  verified: false,
  checkedFirstTime: false,
  data: null,
};

const AddPaymentMethod = (props) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const [isEditingAddress, setIsEditingAddress] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [routingValidateStatus, setRoutingValidateStatus] = useState(INITIAL_VALIDATION_STATE);
  const [prevAddress, setPrevAddress] = useState({});
  const quickpayData = useSelector((state) => state.quickpay.data);
  const uid = useSelector((state) => state.quickpay.uid);
  const hasPaymentMethod = quickpayData.paymentAccounts && quickpayData.paymentAccounts.length > 0;
  const {
    validator: {
      values,
      errors,
      setValues,
      resetErrors,
      deleteErrors,
      setErrors,
      validate,
    },
  } = props;

  const validateRoutingNo = (routingNumber) => {
    const newValue = routingNumber.replace(/[^\d]/g, '');
    if (newValue.length === 9) {
      return true;
    }
    return false;
  };

  const validateRoutingNumber = (routingNo) => {
    let routingNumberUnMask;
    if (routingNo && routingNo.length === 9) {
      routingNumberUnMask = routingNo.replace(/[^\d]/g, '');
    }
    if (!routingNumberUnMask && routingNumberUnMask?.length !== 9) return;
    setRoutingValidateStatus({ ...routingValidateStatus, checkedFirstTime: true, loading: true, data: null });
    setValues({ ...values, bankName: '', isValidRoutingNumber: false });
    if (routingNo) {
      dispatch(validateBankRoutingNumber({
        routingNumber: routingNo,
        success: (res) => {
          const validateRespose = res && res.length > 0 ? res[0] : null;
          const validateStatus = validateRespose && validateRespose.status;
          const validateData = validateRespose && validateRespose.data;

          if (validateStatus && validateStatus === 'success') {
            setValues({ ...values, bankName: validateData.name, routingNumber: routingNo, isValidRoutingNumber: true });
            deleteErrors(['routingNumber']);
            setRoutingValidateStatus({ ...routingValidateStatus, checkedFirstTime: true, verified: true, loading: false, data: validateData });
          }
          if (validateStatus && validateStatus === 'fail') {
            setErrors({ ...errors, routingNumber: 'Routing Number is not valid' });
            setRoutingValidateStatus({ ...routingValidateStatus, checkedFirstTime: true, verified: false, loading: false, data: null });
            setValues({ ...values, bankName: '', routingNumber: routingNo });
          }
        },
        fail: (err) => {
          console.log('err', err);
          setRoutingValidateStatus({ ...routingValidateStatus, checkedFirstTime: true, verified: false, loading: false, data: null });
        },
      }));
    }
  };

  const handleInputChange = (event) => {
    const { onChangeHandler } = props.validator;
    if (event.target.name === 'accountholderName') {
      onChangeHandler(event.target.name, event.target.value.replace(/[^a-zA-Z '-]/g, ''));
    } else if (event.target.name === 'routingNumber' && !routingValidateStatus.loading && event.target.value !== values.routingNumber) {
      const isValidRoutingNumber = validateRoutingNo(event.target.value);
      if (isValidRoutingNumber) {
        validateRoutingNumber(event.target.value);
        onChangeHandler(event.target.name, event.target.value);
      } else {
        setValues({ ...values, routingNumber: event.target.value, bankName: '' });
        setRoutingValidateStatus({ ...routingValidateStatus, verified: false });
      }
    } else {
      onChangeHandler(event.target.name, event.target.value);
    }
  };

  const hasAddress = values.address && values.city && values.state && values.zipCode;
  const isAddressEmpty = !hasAddress || (errors.address === 'The input field is required') || (errors.city === 'The input field is required') || (errors.state === 'The input field is required') || (errors.zip === 'The input field is required');
  const hasAddressError = errors.address || errors.zipCode || errors.city || errors.state;

  useEffect(() => {
    if (quickpayData.addresses && quickpayData.addresses.length > 0) {
      const primaryPhysicalAddress = quickpayData.addresses.find((add) => add.isPrimary && add.type === 'Physical');
      setValues({
        ...values,
        accountholderName: `${quickpayData.firstName} ${quickpayData.lastName}`.trim(),
        address: primaryPhysicalAddress.streetAddress,
        city: primaryPhysicalAddress.city,
        state: primaryPhysicalAddress.state,
        zipCode: primaryPhysicalAddress.zipCode,
      });
    }
  }, []);

  const handleActiveTab = (tab) => {
    if (tab !== activeTab) {
      props.validator.setValues({
        accountholderName: values.accountholderName,
        address: values.address,
        city: values.city,
        state: values.state,
        zipCode: values.zipCode,
      });
      resetErrors();
      setActiveTab(tab);
    }
  };

  const handleEditClick = (e) => {
    e.preventDefault();
    setIsEditingAddress(true);
    setPrevAddress({
      accountholderName: values.accountholderName,
      address: values.address,
      city: values.city,
      state: values.state,
      zipCode: values.zipCode,
    });
  };

  const handleSaveAddClick = (e) => {
    e.preventDefault();

    if (validate(addressSchema).isValid) {
      setIsEditingAddress(false);
    }
  };

  const handleCancelClick = (e) => {
    e.preventDefault();
    setIsEditingAddress(false);
    setValues({
      ...values,
      ...prevAddress,
    });

    if (Object.keys(prevAddress).length === 5) {
      validate(addressSchema, prevAddress);
    }
  };

  const renderTabs = () => (
    <div>
      <h1 className="mb-2">Add Payment Method</h1>
      <ButtonGroup size="sm" className={`d-flex mb-4 ${styles.tab}`}>
        <Button
          color="primary"
          className={styles.btn}
          onClick={handleActiveTab.bind(null, 0)}
          active={activeTab === 0}
          style={{ maxWidth: '50%' }}
        >
          Bank Account
        </Button>
        <Button
          color="primary"
          onClick={handleActiveTab.bind(null, 1)}
          active={activeTab === 1}
          className={styles.btn}
        >
          Debit Card
        </Button>
      </ButtonGroup>
    </div>
  );

  const renderHolderDetails = () => (
    <div className={`${isEditingAddress ? 'isOpen' : ''} active addressWrapper has-value ${(hasAddressError) && 'addressRequired'}`}>
      <label>
        <span>
          {activeTab === 0 ? 'Account Holder Details' : 'Cardholder Details'}
          <em>{hasAddressError ? 'Error' : 'Required'}</em>
        </span>
        <div className={styles.addressBtn}>
          {isEditingAddress ? (
            <>
              <button type="button" className="btn-sm" onClick={handleCancelClick}>Cancel</button>
              <button type="button" className="btn-sm" onClick={handleSaveAddClick}>Save</button>
            </>
          ) : (
            <button type="button" className="btn-sm" onClick={handleEditClick}>Edit</button>
          )}
        </div>
      </label>
      {
        isEditingAddress
          ? (
            <div className="faux-input">
              <Input
                name="accountholderName"
                labelClassName="mb-2"
                label={activeTab === 0 ? 'Account Holder Name' : 'Cardholder Name'}
                value={values.accountholderName}
                onChange={handleInputChange}
                placeHolder={activeTab === 0 ? 'Account Holder Name' : 'Cardholder Name'}
                isRequired
                hasError={!!errors.accountholderName}
                errorMessage={errors.accountholderName}
              />
              <Input
                labelClassName="mb-2"
                label="Street Address"
                value={`${values.address || ''}`}
                name="address"
                placeholder="Address"
                onChange={handleInputChange}
                isRequired
                hasError={errors.address}
              />
              <Input
                labelClassName="mb-2"
                label="City"
                value={`${values.city || ''}`}
                name="city"
                placeholder="City"
                onChange={handleInputChange}
                isRequired
                hasError={errors.city}
              />
              <Select
                className="mb-2"
                data={states}
                label="State"
                onChange={handleInputChange}
                value={values.state || ''}
                name="state"
                isRequired
                hasError={errors.state}
                errorMessage={errors.state}
              />
              <Input
                label="Zipcode"
                value={`${values.zipCode || ''}`}
                type="tel"
                name="zipCode"
                isMasked={[/\d/, /\d/, /\d/, /\d/, /\d/]}
                placeholder="_____"
                onChange={handleInputChange}
                isRequired
                hasError={errors.zipCode}
                inputMode="numeric"
              />
            </div>
          )
          : (
            <div className="faux-input">
              {values.accountholderName ? (
                <>
                  {values.accountholderName}
                  <br />
                </>
              ) : null}
              {isAddressEmpty ? 'Address not provided' : (
                <>
                  {values.address}
                  {' '}
                  <br />
                  {values.city}
                  ,
                  {' '}
                  {values.state}
                  {' '}
                  {values.zipCode}
                </>
              )}
            </div>
          )
      }
    </div>
  );

  const cardNumber = numberUnmask(values.cardNumber || '');
  let cardImg = null;

  let errorCardMessage = '';
  if (`${cardNumber}`.startsWith('3')) {
    cardImg = amxIcon;
    errorCardMessage = 'Currently we accept only Visa or Mastercard';
  } else if (`${cardNumber}`.startsWith('4')) {
    cardImg = visaCardIcon;
    errorCardMessage = '';
  } else if (`${cardNumber}`.startsWith('5')) {
    cardImg = masterCardIcon;
    errorCardMessage = '';
  } else if (`${cardNumber}`.startsWith('6')) {
    cardImg = discoverIcon;
    errorCardMessage = 'Currently we accept only Visa or Mastercard';
  } else if (`${cardNumber}`.length > 3 && values.isEditingDebitCard) {
    errorCardMessage = 'Currently we accept only Visa or Mastercard';
  }

  const renderDebitCard = () => (
    <div>
      <div className="payment-logo position-relative">
        <Input
          labelClassName="mb-2"
          name="cardNumber"
          label="Card Number"
          value={values.cardNumber || ''}
          isMasked={cardNumberMask}
          onChange={handleInputChange}
          placeHolder="Card Number"
          isRequired
          inputMode="numeric"
          hasError={!!errors.cardNumber}
          errorMessage={errors.cardNumber || errorCardMessage}
        />
        <div className="card-container">
          {(cardNumber.length > 3 && cardImg) && <img src={cardImg} height="28px" alt="card" />}
        </div>
      </div>
      <Input
        name="expirationDate"
        label="Expiration Date"
        onChange={handleInputChange}
        value={values.expirationDate || ''}
        isMasked={[/\d/, /\d/, '/', /\d/, /\d/, /\d/, /\d/]}
        isRequired
        hasError={!!errors.expirationDate}
        errorMessage={errors.expirationDate}
        placeHolder="MM/YYYY"
        inputMode="numeric"
      />
    </div>
  );

  const renderBankAccount = () => (
    <div>
      <Select
        className="mb-2"
        name="accountType"
        data={[
          { value: 'checking', title: 'Checking' },
          { value: 'savings', title: 'Savings' },
        ]}
        value={values.accountType}
        onChange={handleInputChange}
        label="Account Type"
        isRequired
        hasError={!!errors.accountType}
        errorMessage={errors.accountType}
      />
      <div className="position-relative">
        <Input
          labelClassName="mb-2"
          name="routingNumber"
          label="Routing Number"
          value={values.routingNumber}
          isMasked={[/\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/]}
          onChange={handleInputChange}
          placeHolder="Routing Number"
          inputMode="numeric"
          isRequired
          hasError={!!errors.routingNumber}
          errorMessage={errors.routingNumber}
        />
        {values.bankName ? (
          <div className={styles.bankName}>
            <span>{values.bankName}</span>
          </div>
        ) : null}
        {routingValidateStatus.loading
          ? <span className="input-loader" />
          : routingValidateStatus.checkedFirstTime
            ? (
              <span className="input-status">
                <img
                  src={`${routingValidateStatus.verified ? VerifiedIcon : WarningIcon}`}
                  alt="input-status"
                />
              </span>
            )
            : null}
      </div>
      <Input
        labelClassName="mb-2"
        name="accountNumber"
        label="Account Number"
        inputMode="numeric"
        value={values.accountNumber}
        onChange={handleInputChange}
        placeHolder="Account Number"
        isMasked={numberMask}
        isRequired
        hasError={!!errors.accountNumber}
        errorMessage={errors.accountNumber}
      />
    </div>
  );

  const renderTabContent = () => (activeTab === 0 ? renderBankAccount() : renderDebitCard());

  const handleAddPaymentMethod = () => {
    const isBankAccount = activeTab === 0;
    const schema = isBankAccount ? addPaymentMethodSchema : addDebitCardSchema;
    setErrorMsg('');
    if (validate(schema).isValid) {
      setIsLoading(true);
      const data = {
        ...values,
        accountholderName: `${values.accountholderName}`.replace(/^\s*\/*\s*|\s*\/*\s*$/gm, '').trim(),
        category: isBankAccount ? 'bankaccount' : 'bankcard',
        accountType: isBankAccount ? values.accountType : 'debit',
        cardNumber: isBankAccount ? undefined : numberUnmask(values.cardNumber),
        expirationDate: isBankAccount ? undefined : {
          month: values.expirationDate.split('/')[0],
          year: values.expirationDate.split('/')[1],
        },
      };
      dispatch(addPaymentAccountAction({
        data,
        success: () => {
          setIsLoading(false);
          history.push('/quickpay/simple-pay');
        },
        fail: (error) => {
          setErrorMsg(get(error, 'data.message', 'An error occurred.'));
          setIsLoading(false);
        },
      }));
    }
  };

  const goToSimplePay = (e) => {
    e.preventDefault();
    if (uid) {
      history.push({
        pathname: `/quickpay/${uid}`,
        state: {
          from: 'quickpay/simple-pay',
        },
      });
    }
  };

  return (
    <div className={styles.quickpay}>
      <div className={cn(styles.content, 'text-center')}>
        <h2>
          Hey
          {' '}
          {quickpayData.firstName}
          !
        </h2>
        {!hasPaymentMethod ? (
          <p>You do not have a payment method on file.</p>
        ) : null}
        {renderTabs()}
        {renderHolderDetails()}
        {renderTabContent()}
      </div>
      <div className={styles.stickyFooter}>
        {errorMsg ? (
          <div className={styles.error}>
            {errorMsg}
          </div>
        ) : null}
        <Button
          color="primary"
          className="w-100 mt-1"
          onClick={handleAddPaymentMethod}
          isLoading={isLoading}
        >
          Add Payment Method
        </Button>
        {hasPaymentMethod ? (
          <span
            className={`${styles.anchorBtn} mt-1 d-block`}
            onClick={goToSimplePay}
          >
            Cancel
          </span>
        ) : null}
      </div>
    </div>
  );
};

AddPaymentMethod.propTypes = {
  validator: PropTypes.object.isRequired,
};

export default compose(Validator())(AddPaymentMethod);
